import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Menu, X, ChevronDown } from "lucide-react";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import Logo from "@/components/Logo";
import UserMenu from "@/components/UserMenu";

const toSlug = (label: string) =>
  label.toLowerCase().replace(/\s+/g, "-").replace(/[']/g, "");

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [megaOpen, setMegaOpen] = useState(false);
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t } = useTranslation();
  const megaRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);

  const occasionsData = {
    occasions: [
      "anniversary", "baby", "bonVoyage", "congratulations", "engagement",
      "getWell", "goodLuck", "graduation", "holiday", "sympathy",
      "thankYou", "wedding",
    ],
    forTeams: ["employeeAppreciation", "farewell", "retirement", "welcomeToTeam", "wellDone", "workAnniversary"],
    forParents: ["coachCards", "teacher"],
    birthdays: [
      "kidsBirthday", "18thBirthday", "21stBirthday", "30thBirthday",
      "40thBirthday", "50thBirthday", "60thBirthday", "70thBirthday",
      "80thBirthday", "funnyBirthday",
    ],
    trending: ["corporateFarewell", "funnyFarewellCards", "photoUpload", "christmas"],
  };

  const trendingTags: Record<string, string> = {
    holiday: t("megaMenu.tags.trending"),
    farewell: t("megaMenu.tags.popular"),
  };

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (
        megaRef.current &&
        !megaRef.current.contains(e.target as Node) &&
        triggerRef.current &&
        !triggerRef.current.contains(e.target as Node)
      ) {
        setMegaOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const MegaMenuLink = ({ labelKey, tag }: { labelKey: string; tag?: string }) => (
    <button
      onClick={() => { navigate(`/cards/${toSlug(t(`occasions.${labelKey}`))}`); setMegaOpen(false); }}
      className="flex items-center gap-2 text-xs font-light text-muted-foreground hover:text-foreground transition-colors py-1 text-start tracking-wide"
    >
      {t(`occasions.${labelKey}`)}
      {tag && (
        <span className="text-[9px] font-normal tracking-[0.15em] uppercase bg-secondary text-muted-foreground px-2 py-0.5 rounded-full">
          {tag}
        </span>
      )}
    </button>
  );

  const MegaMenuSection = ({ title, items, isBold }: { title: string; items: string[]; isBold?: boolean }) => (
    <div>
      <h4 className="text-[10px] font-normal text-muted-foreground mb-4 uppercase tracking-[0.2em]">{title}</h4>
      <div className="flex flex-col gap-1">
        {items.map((item) => (
          <MegaMenuLink key={item} labelKey={item} tag={trendingTags[item]} />
        ))}
        {isBold && (
          <button
            onClick={() => { navigate("/cards/birthday"); setMegaOpen(false); }}
            className="text-xs font-normal text-foreground hover:text-muted-foreground transition-colors pt-3 text-start tracking-wide"
          >
            {t("nav.allBirthdayCards")}
          </button>
        )}
      </div>
    </div>
  );

  return (
    <nav className="sticky top-0 z-50 bg-background/98 backdrop-blur-sm border-b border-border/40">
      <div className="container mx-auto flex items-center justify-between py-6 px-4">
        <a href="/" className="flex items-center gap-2">
          <Logo />
        </a>

        {/* Desktop */}
        <div className="hidden md:flex items-center gap-10">
          <a href="#how-it-works" className="text-xs font-light text-muted-foreground hover:text-foreground transition-colors tracking-wide">
            {t("nav.howItWorks")}
          </a>
          <button
            ref={triggerRef}
            onClick={() => setMegaOpen(!megaOpen)}
            className="flex items-center gap-1 text-xs font-light text-muted-foreground hover:text-foreground transition-colors tracking-wide"
          >
            {t("nav.browseOccasions")}
            <ChevronDown size={12} strokeWidth={1.5} className={`transition-transform ${megaOpen ? "rotate-180" : ""}`} />
          </button>
          <a href="#pricing" className="text-xs font-light text-muted-foreground hover:text-foreground transition-colors tracking-wide">
            {t("nav.pricing")}
          </a>
          <a href="#faq" className="text-xs font-light text-muted-foreground hover:text-foreground transition-colors tracking-wide">
            {t("nav.faq")}
          </a>
        </div>

        <div className="hidden md:flex items-center gap-3">
          <LanguageSwitcher />
          {user ? (
            <UserMenu />
          ) : (
            <Button variant="ghost" size="sm" className="text-xs font-light text-muted-foreground tracking-wide" onClick={() => navigate("/auth")}>{t("nav.signIn")}</Button>
          )}
          <Button
            size="sm"
            onClick={() => navigate("/create")}
            className="rounded-full px-6 bg-foreground text-background hover:bg-foreground/85 text-xs font-light tracking-wide shadow-none"
          >
            {t("nav.startCard")}
          </Button>
        </div>

        {/* Mobile toggle */}
        <div className="md:hidden flex items-center gap-3">
          <LanguageSwitcher />
          <button className="text-foreground" onClick={() => setIsOpen(!isOpen)}>
            {isOpen ? <X size={20} strokeWidth={1.5} /> : <Menu size={20} strokeWidth={1.5} />}
          </button>
        </div>
      </div>

      {/* Mega Menu */}
      {megaOpen && (
        <div ref={megaRef} className="hidden md:block absolute left-0 right-0 top-full bg-background border-b border-border/40 z-50 animate-fade-in">
          <div className="container mx-auto px-4 py-10">
            <div className="grid grid-cols-5 gap-10">
              <div className="flex flex-col items-center justify-center">
                <div className="bg-secondary rounded-xl overflow-hidden w-full aspect-[3/4] flex items-center justify-center mb-4">
                  <div className="text-center p-4">
                    <span className="text-[9px] uppercase tracking-[0.2em] font-normal text-muted-foreground">{t("nav.trending")}</span>
                    <p className="text-sm font-light text-foreground mt-2">{t("nav.farewellCards")}</p>
                  </div>
                </div>
                <Button size="sm" variant="outline" className="rounded-full text-xs font-light tracking-wide shadow-none" onClick={() => setMegaOpen(false)}>
                  {t("nav.browse")}
                </Button>
              </div>
              <div>
                <h4 className="text-[10px] font-normal text-muted-foreground mb-4 uppercase tracking-[0.2em]">{t("megaMenu.occasions")}</h4>
                <div className="flex flex-col gap-1">
                  {occasionsData.occasions.map((item) => (
                    <MegaMenuLink key={item} labelKey={item} tag={trendingTags[item]} />
                  ))}
                </div>
              </div>
              <div className="space-y-8">
                <MegaMenuSection title={t("megaMenu.forTeams")} items={occasionsData.forTeams} />
                <MegaMenuSection title={t("megaMenu.forParents")} items={occasionsData.forParents} />
              </div>
              <MegaMenuSection title={t("megaMenu.birthdays")} items={occasionsData.birthdays} isBold />
              <div className="space-y-8">
                <MegaMenuSection title={t("megaMenu.trendingLabel")} items={occasionsData.trending} />
                <div>
                  <h4 className="text-[10px] font-normal text-muted-foreground mb-4 uppercase tracking-[0.2em]">{t("megaMenu.helpfulInfo")}</h4>
                  <div className="flex flex-col gap-1">
                    <button onClick={() => { navigate("/"); setMegaOpen(false); }} className="text-xs font-light text-muted-foreground hover:text-foreground transition-colors py-1 text-start tracking-wide">
                      {t("nav.aboutGroupCards")}
                    </button>
                    <button onClick={() => { navigate("/cards/birthday"); setMegaOpen(false); }} className="text-xs font-normal text-foreground hover:text-muted-foreground transition-colors py-1 text-start tracking-wide">
                      {t("nav.browseAllCards")}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Mobile menu */}
      {isOpen && (
        <div className="md:hidden border-t border-border/40 bg-background px-4 pb-6">
          <a href="#how-it-works" className="block py-3 text-sm font-light text-muted-foreground hover:text-foreground tracking-wide" onClick={() => setIsOpen(false)}>
            {t("nav.howItWorks")}
          </a>
          <a href="#pricing" className="block py-3 text-sm font-light text-muted-foreground hover:text-foreground tracking-wide" onClick={() => setIsOpen(false)}>
            {t("nav.pricing")}
          </a>
          <a href="#faq" className="block py-3 text-sm font-light text-muted-foreground hover:text-foreground tracking-wide" onClick={() => setIsOpen(false)}>
            {t("nav.faq")}
          </a>
          <a href="#occasions" className="block py-3 text-sm font-light text-muted-foreground hover:text-foreground tracking-wide" onClick={() => setIsOpen(false)}>
            {t("nav.browseOccasions")}
          </a>
          <div className="flex flex-col gap-2 pt-4">
            {user ? (
              <>
                <button onClick={() => { navigate("/dashboard"); setIsOpen(false); }} className="flex items-center gap-3 py-2">
                  <UserMenu />
                  <span className="text-sm text-foreground">{t("nav.dashboard", { defaultValue: "Dashboard" })}</span>
                </button>
              </>
            ) : (
              <Button variant="ghost" size="sm" className="font-light" onClick={() => { navigate("/auth"); setIsOpen(false); }}>{t("nav.signIn")}</Button>
            )}
            <Button
              size="sm"
              className="rounded-full bg-foreground text-background hover:bg-foreground/85 font-light shadow-none"
              onClick={() => { navigate("/create"); setIsOpen(false); }}
            >
              {t("nav.startCard")}
            </Button>
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;
